import cn from 'classnames';
import React from 'react';
import { useLabel } from 'react-aria';
import { ICON_CLASSNAME } from './constants';
import * as S from './styles';
import { ActionButtonProps } from './types';

function ForwardedActionButton<
  T extends Element = HTMLButtonElement,
  As extends React.ElementType = 'button',
>(
  props: ActionButtonProps<As>,
  ref: React.ForwardedRef<T>,
): React.ReactElement | null {
  const { as, children, color, label, variant, ...restProps } = props;

  const { labelProps, fieldProps } = useLabel(props);

  const button = (
    <S.Root
      forwardedAs={as ?? 'button'}
      $color={color}
      $variant={variant}
      ref={ref}
      {...restProps}
    >
      <S.Inner $color={color} $variant={variant}>
        {React.isValidElement<{ className?: string }>(children) &&
          React.cloneElement(children, {
            className: cn(ICON_CLASSNAME, children.props.className),
          })}
      </S.Inner>
    </S.Root>
  );

  if (!label) {
    return button;
  }

  return (
    <S.Container>
      {React.cloneElement(button, fieldProps)}
      <S.Label {...labelProps}>{label}</S.Label>
    </S.Container>
  );
}

/**
 * Accessible action button.
 *
 * If a `label` prop is passed, its contents is rendered along with the button
 * and is linked to the button as its label. Without a label, `aria-label` or
 * `aria-labelledby` should be used.
 */
export const ActionButton = React.forwardRef(ForwardedActionButton) as <
  T extends Element = HTMLButtonElement,
  As extends React.ElementType = 'button',
>(
  props: ActionButtonProps<As> & { ref?: React.Ref<T> },
) => React.ReactElement | null;

export default ActionButton;
